import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import './EcosystemSection.css'



const API = process.env.REACT_APP_API_URL || "";

// ── Keys match what's seeded in MongoDB (section: "ecosystem") ───────────────
const CARDS = [
    {
        id: 'flyhub', accent: '#e85d26', rgb: '232,93,38',
        tag: 'MARKETPLACE', name: 'Flyhub',
        tagline: "India's drone marketplace.",
        desc: 'Buy and sell drones, parts and accessories. Built for pilots, dealers and manufacturers.',
        url: 'https://flyhub.info/', urlLabel: 'flyhub.info',
        badges: [{ label: 'Site Live', type: 'live' }, { label: 'App Coming Soon', type: 'soon' }],
        logoKey: 'flyhub-logo', screenKeys: ['flyhub-screen-1', 'flyhub-screen-2'],
        type: 'phone',
    },
    {
        id: 'airspace', accent: '#0ea5e9', rgb: '14,165,233',
        tag: 'AIRSPACE INTEL', name: 'AirspaceMap',
        tagline: "India's airspace intelligence.",
        desc: 'Interactive map of red zones, green zones and controlled airspace — built for Indian drone operators.',
        url: 'https://airspacemap.in/', urlLabel: 'airspacemap.in',
        badges: [{ label: 'Site Live', type: 'live' }, { label: 'India Coverage', type: 'info' }],
        logoKey: 'airspacemap-logo', screenKeys: [], type: 'map',
    },
    {
        id: 'flytutor', accent: '#5d179e', rgb: '93,23,158',
        tag: 'PILOT TRAINING', name: 'Flytutor',
        tagline: 'DGCA approved drone training.',
        desc: "Flytutor is India's pioneer in drone training and AI-driven drone solutions, offering expert-led courses in aerial photography, hybrid drone technology and advanced drone innovation.",
        url: 'https://flytutor.in/', urlLabel: 'flytutor.in',
        badges: [{ label: 'DGCA Approved RPTO', type: 'cert' }],
        logoKey: 'flytutor-logo', screenKeys: [],
        courses: [
            { label: 'Small Category RPC', url: 'https://flytutor.in/course-enroll-template/68d3aff36059f48a7ca2d12f' },
            { label: 'Medium Category RPC', url: 'https://flytutor.in/course-enroll-template/68d3b0a86059f48a7ca2d135' },
            { label: 'Aerial Mapping RPC', url: 'https://flytutor.in/certificate-enroll-template/68d3b2546059f48a7ca2d141' },
            { label: 'RPC Upgradation', url: 'https://flytutor.in/course-enroll-template/68d3b1bf6059f48a7ca2d13b' },
        ],
        dgcaKey: 'dgca-badge', type: 'courses',
    },
    // {
    //     id: 'solarmark', accent: '#f68708ff', rgb: '246,135,8',
    //     tag: 'SOLAR INSPECTION', name: 'SolarMark',
    //     tagline: "AI-powered solar inspection.",
    //     desc: 'Elevating solar asset management with aerospace-grade drone thermal imaging and AI-driven precision. Fast, reliable, and exceptionally accurate inspections.',
    //     url: 'https://solarmark.in/', urlLabel: 'solarmark.in',
    //     badges: [{ label: 'Site Live', type: 'live' }, { label: 'AI-Powered', type: 'info' }],
    //     logoKey: 'solarmark_logo', screenKeys: [], type: 'solar',
    // },

    {
        id: 'agrisim',
        accent: '#22c55e', // agriculture green
        rgb: '34,197,94',

        tag: 'SIMULATION',
        name: 'Agri-Sim',

        tagline: 'Agri drone training. Powered by real physics.',

        desc: 'A physics-driven agricultural drone simulator built for RPTOs and beginners. Train using real transmitter controls, practice spraying operations, and build flight confidence in a safe virtual environment.',

        url: '/businesses/agri-sim',
        urlLabel: 'Explore Simulator',
        internal: true,

        badges: [
            { label: 'In Development', type: 'soon' },
            { label: 'Physics-Based', type: 'info' },
            { label: 'RPTO Focused', type: 'info' }
        ],

        logoKey: 'agrisim-logo',

        screenKeys: [
            'agrisim-screen-1',
            'agrisim-screen-2'
        ],

        type: 'laptop'
    }
]

// ── Fetch all ecosystem images from API in one call ───────────────────────────
function useImages() {
    const [urls, setUrls] = useState({})
    useEffect(() => {
        Promise.all([
            fetch(`${API}/api/media/ecosystem`).then(res => res.ok ? res.json() : {}),
            fetch(`${API}/api/media/solarmark`).then(res => res.ok ? res.json() : {}),
            fetch(`${API}/api/media/agrisim`).then(res => res.ok ? res.json() : {}),
        ])
            .then(([ecoMap, solarMap, agrisimMap]) =>
                setUrls({ ...ecoMap, ...solarMap, ...agrisimMap })
            )
            .catch(() => { })
    }, [])
    return urls
}

function PhoneMockup({ screen1, screen2, accent }) {
    const [slide, setSlide] = useState(0)
    useEffect(() => {
        if (!screen1 && !screen2) return
        const t = setInterval(() => setSlide(s => (s + 1) % 2), 2800)
        return () => clearInterval(t)
    }, [screen1, screen2])
    const screens = [screen1, screen2].filter(Boolean)
    const current = screens[slide % Math.max(screens.length, 1)]

    return (
        <div className="eco-phone">
            <div className="eco-phone-frame" style={{ '--a': accent }}>
                <div className="eco-phone-notch" />
                <div className="eco-phone-screen">
                    {current && current !== 'error'
                        ? <img src={current} alt="App screen" className="eco-phone-img" key={slide} />
                        : <div className="eco-phone-placeholder" />
                    }
                    <div className="eco-phone-shine" />
                </div>
                <div className="eco-phone-bar" style={{ background: accent }} />
            </div>
            {screens.length > 1 && (
                <div className="eco-phone-dots">
                    {screens.map((_, i) => (
                        <span key={i}
                            className={`eco-phone-dot ${i === slide % screens.length ? 'eco-phone-dot--on' : ''}`}
                            style={{ background: i === slide % screens.length ? accent : undefined }}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}

function MapVisual({ accent }) {
    return (
        <div className="eco-map">
            <svg viewBox="0 0 300 200" className="eco-map-canvas" xmlns="http://www.w3.org/2000/svg">
                <defs>
                    <pattern id="grid" width="24" height="24" patternUnits="userSpaceOnUse">
                        <path d="M 24 0 L 0 0 0 24" fill="none" stroke="rgba(14,165,233,0.08)" strokeWidth="1" />
                    </pattern>
                </defs>
                <rect width="300" height="200" fill="url(#grid)" />
                <polygon points="30,80 120,30 210,50 270,100 210,160 120,180 45,140"
                    fill="none" stroke="rgba(14,165,233,0.35)" strokeWidth="1.5" strokeDasharray="4,4" />
                <rect x="30" y="102" width="60" height="36" fill="rgba(34,197,94,0.12)" stroke="rgba(34,197,94,0.45)" strokeWidth="1" />
                <text x="60" y="123" textAnchor="middle" fill="rgba(34,197,94,0.8)" fontSize="7" fontFamily="IBM Plex Mono">GREEN</text>
                <rect x="125" y="117" width="80" height="28" fill="rgba(34,197,94,0.12)" stroke="rgba(34,197,94,0.45)" strokeWidth="1" />
                <text x="165" y="134" textAnchor="middle" fill="rgba(34,197,94,0.8)" fontSize="7" fontFamily="IBM Plex Mono">GREEN</text>
                <circle cx="90" cy="60" r="24" fill="rgba(239,68,68,0.12)" stroke="rgba(239,68,68,0.5)" strokeWidth="1.5" />
                <text x="90" y="63" textAnchor="middle" fill="rgba(239,68,68,0.8)" fontSize="7" fontFamily="IBM Plex Mono">NO FLY</text>
                <circle cx="195" cy="90" r="18" fill="rgba(239,68,68,0.12)" stroke="rgba(239,68,68,0.5)" strokeWidth="1.5" />
                <text x="195" y="93" textAnchor="middle" fill="rgba(239,68,68,0.8)" fontSize="6" fontFamily="IBM Plex Mono">NO FLY</text>
                <circle cx="150" cy="120" r="12" fill="none" stroke={accent + '44'} strokeWidth="2" />
                <circle cx="150" cy="120" r="5" fill={accent} />
            </svg>
            <div className="eco-map-legend">
                <span className="eco-map-leg eco-map-leg--red">● No-fly</span>
                <span className="eco-map-leg eco-map-leg--green">● Green</span>
                <span className="eco-map-leg eco-map-leg--blue">● Controlled</span>
            </div>
        </div>
    )
}

function CoursesVisual({ courses, accent, dgcaUrl }) {
    return (
        <div className="eco-courses">
            {courses.map((c, i) => (
                <a key={i} href={c.url} target="_blank" rel="noopener noreferrer"
                    className="eco-course eco-course--link" style={{ '--a': accent }}>
                    <span className="eco-course-num" style={{ color: accent }}>{String(i + 1).padStart(2, '0')}</span>
                    <span className="eco-course-label">{c.label}</span>
                    <span className="eco-course-arrow">→</span>
                </a>
            ))}
            {dgcaUrl && dgcaUrl !== 'error' && <img src={dgcaUrl} alt="DGCA" className="eco-dgca" />}
        </div>
    )
}

// ── Solar panel grid visual with AI scan overlay ──────────────────────────────
function SolarVisual({ accent }) {
    // 4 columns × 3 rows of panels. Index 7 is flagged as a fault.
    const PANELS = Array.from({ length: 12 }, (_, i) => ({
        id: i,
        fault: i === 7,
        warm: i === 2 || i === 10,
    }))

    return (
        <div className="eco-solar">
            <svg viewBox="0 0 300 200" className="eco-solar-canvas" xmlns="http://www.w3.org/2000/svg">
                <defs>
                    {/* subtle background grid */}
                    <pattern id="solar-grid" width="20" height="20" patternUnits="userSpaceOnUse">
                        <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(246,135,8,0.06)" strokeWidth="0.5" />
                    </pattern>

                    {/* scan-line gradient — animates top→bottom */}
                    <linearGradient id="scan-grad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="transparent" />
                        <stop offset="48%" stopColor="transparent" />
                        <stop offset="50%" stopColor={accent} stopOpacity="0.18" />
                        <stop offset="52%" stopColor="transparent" />
                        <stop offset="100%" stopColor="transparent" />
                    </linearGradient>
                </defs>

                <rect width="300" height="200" fill="url(#solar-grid)" />

                {/* Solar panels — 4 cols × 3 rows */}
                {PANELS.map((p) => {
                    const col = p.id % 4
                    const row = Math.floor(p.id / 4)
                    const x = 24 + col * 66
                    const y = 18 + row * 58
                    const fill = p.fault
                        ? 'rgba(239,68,68,0.15)'
                        : p.warm
                            ? `rgba(246,135,8,0.13)`
                            : 'rgba(246,135,8,0.05)'
                    const stroke = p.fault
                        ? 'rgba(239,68,68,0.7)'
                        : p.warm
                            ? accent + 'aa'
                            : accent + '44'

                    return (
                        <g key={p.id}>
                            <rect x={x} y={y} width={56} height={46}
                                fill={fill} stroke={stroke} strokeWidth="1" rx="2" />
                            {/* internal cell lines */}
                            <line x1={x + 19} y1={y + 1} x2={x + 19} y2={y + 45} stroke={stroke} strokeWidth="0.5" />
                            <line x1={x + 37} y1={y + 1} x2={x + 37} y2={y + 45} stroke={stroke} strokeWidth="0.5" />
                            <line x1={x + 1} y1={y + 15} x2={x + 55} y2={y + 15} stroke={stroke} strokeWidth="0.5" />
                            <line x1={x + 1} y1={y + 30} x2={x + 55} y2={y + 30} stroke={stroke} strokeWidth="0.5" />
                            {/* fault marker */}
                            {p.fault && (
                                <text x={x + 28} y={y + 27} textAnchor="middle"
                                    fill="rgba(239,68,68,0.9)" fontSize="9" fontFamily="IBM Plex Mono" fontWeight="bold">
                                    ✕
                                </text>
                            )}
                            {/* warm marker */}
                            {p.warm && !p.fault && (
                                <circle cx={x + 46} cy={y + 8} r="4"
                                    fill={accent} opacity="0.7" />
                            )}
                        </g>
                    )
                })}

                {/* AI scan line — CSS animation moves it */}
                <rect className="eco-solar-scan" x="0" y="0" width="300" height="200"
                    fill="url(#scan-grad)" />

                {/* Drone silhouette */}
                <g transform="translate(220, 158)" opacity="0.55">
                    <circle cx="0" cy="0" r="4" fill="none" stroke={accent} strokeWidth="1.2" />
                    <line x1="-4" y1="0" x2="-10" y2="-5" stroke={accent} strokeWidth="1" />
                    <line x1="4" y1="0" x2="10" y2="-5" stroke={accent} strokeWidth="1" />
                    <line x1="-4" y1="0" x2="-10" y2="5" stroke={accent} strokeWidth="1" />
                    <line x1="4" y1="0" x2="10" y2="5" stroke={accent} strokeWidth="1" />
                    <circle cx="-10" cy="-5" r="2.5" fill="none" stroke={accent} strokeWidth="0.8" />
                    <circle cx="10" cy="-5" r="2.5" fill="none" stroke={accent} strokeWidth="0.8" />
                    <circle cx="-10" cy="5" r="2.5" fill="none" stroke={accent} strokeWidth="0.8" />
                    <circle cx="10" cy="5" r="2.5" fill="none" stroke={accent} strokeWidth="0.8" />
                </g>

                {/* Fault callout line */}
                <line x1="80" y1="120" x2="220" y2="158" stroke={accent} strokeWidth="0.6"
                    strokeDasharray="3,3" opacity="0.4" />
            </svg>

            <div className="eco-solar-legend">
                <span className="eco-solar-leg eco-solar-leg--ok" style={{ color: accent }}>● Normal</span>
                <span className="eco-solar-leg eco-solar-leg--warm" style={{ color: accent }}>● Hotspot</span>
                <span className="eco-solar-leg eco-solar-leg--fault">● Fault</span>
            </div>
        </div>
    )
}

function LaptopMockup({ screen1, screen2 }) {
    const [slide, setSlide] = useState(0)

    useEffect(() => {
        if (!screen1 && !screen2) return
        const t = setInterval(() => setSlide(s => (s + 1) % 2), 3000)
        return () => clearInterval(t)
    }, [screen1, screen2])

    const screens = [screen1, screen2].filter(Boolean)
    const current = screens[slide % Math.max(screens.length, 1)]

    return (
        <div className="eco-laptop">
            <div className="eco-laptop-screen">
                {current && current !== 'error' ? (
                    <img src={current} alt="Simulator screen" />
                ) : (
                    <div className="eco-laptop-placeholder" />
                )}
            </div>
            <div className="eco-laptop-base" />
        </div>
    )
}

function EcoCard({ data, urls, index }) {
    const cardRef = useRef(null)
    const readyRef = useRef(false)

    useEffect(() => {
        const el = cardRef.current
        if (!el) return
        gsap.fromTo(el,
            { opacity: 0, y: 50 },
            {
                opacity: 1, y: 0, duration: 0.9, ease: 'power3.out',
                delay: index * 0.12,
                onComplete: () => { readyRef.current = true },
                scrollTrigger: {
                    trigger: el, start: 'top 88%',
                    toggleActions: 'play none none none',
                },
            }
        )
    }, [index])

    const onMouseEnter = () => {
        if (!readyRef.current) return
        gsap.to(cardRef.current, { y: -10, scale: 1.018, duration: 0.45, ease: 'power3.out', overwrite: 'auto' })
    }
    const onMouseLeave = () => {
        if (!readyRef.current) return
        gsap.to(cardRef.current, { y: 0, scale: 1, duration: 0.55, ease: 'expo.out', overwrite: 'auto' })
    }

    const logo = urls[data.logoKey]
    const screen1 = data.screenKeys?.[0] ? urls[data.screenKeys[0]] : null
    const screen2 = data.screenKeys?.[1] ? urls[data.screenKeys[1]] : null
    const dgcaUrl = data.dgcaKey ? urls[data.dgcaKey] : null

    return (
        <div ref={cardRef} className="eco-card"
            style={{ '--a': data.accent, '--rgb': data.rgb, opacity: 0 }}
            onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}
        >
            <div className="eco-card-glow" />
            <div className="eco-card-top">
                <span className="eco-card-tag" style={{ color: data.accent, borderColor: `rgba(${data.rgb},0.3)` }}>
                    {data.tag}
                </span>
                <div className="eco-card-logo">
                    {logo && logo !== 'error'
                        ? <img src={logo} alt={data.name} className="eco-logo-img" />
                        : <span className="eco-logo-text" style={{ color: data.accent }}>{data.name}</span>
                    }
                </div>
            </div>

            <div className="eco-card-visual">
                {data.type === 'phone' && <PhoneMockup screen1={screen1} screen2={screen2} accent={data.accent} />}
                {data.type === 'map' && <MapVisual accent={data.accent} />}
                {data.type === 'courses' && <CoursesVisual courses={data.courses} accent={data.accent} dgcaUrl={dgcaUrl} />}
                {data.type === 'solar' && <SolarVisual accent={data.accent} />}
                {data.type === 'laptop' && (
                    <LaptopMockup
                        screen1={screen1}
                        screen2={screen2}
                    />
                )}
            </div>

            <div className="eco-card-info">
                <h3 className="eco-card-name">{data.name}</h3>
                <p className="eco-card-tagline" style={{ color: data.accent }}>{data.tagline}</p>
                <p className="eco-card-desc">{data.desc}</p>
                <div className="eco-card-badges">
                    {data.badges.map((b, i) => (
                        <span key={i} className={`eco-badge eco-badge--${b.type}`}
                            style={b.type === 'live' || b.type === 'cert' ? { borderColor: data.accent, color: data.accent } : {}}>
                            {(b.type === 'live' || b.type === 'cert') && <span className="eco-badge-pulse" style={{ background: data.accent }} />}
                            {b.label}
                        </span>
                    ))}
                </div>
                {data.internal ? (
                    <Link
                        to={data.url}
                        className="eco-card-link"
                        style={{ color: data.accent }}
                    >
                        <span>{data.urlLabel}</span>
                        <span className="eco-link-arrow">→</span>
                    </Link>
                ) : (
                    <a
                        href={data.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="eco-card-link"
                        style={{ color: data.accent }}
                    >
                        <span>{data.urlLabel}</span>
                        <span className="eco-link-arrow">→</span>
                    </a>
                )}
            </div>

            <div className="eco-card-line" style={{ background: data.accent }} />
        </div>
    )
}

export default function EcosystemSection() {
    const wrapRef = useRef(null)
    const headRef = useRef(null)
    const h2Ref = useRef(null)
    const subRef = useRef(null)
    const urls = useImages()

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.fromTo(headRef.current, { opacity: 0, y: 30 },
                {
                    opacity: 1, y: 0, duration: 0.8, ease: 'expo.out',
                    scrollTrigger: { trigger: headRef.current, start: 'top 88%', toggleActions: 'play none none none' }
                })
            gsap.fromTo(h2Ref.current, { opacity: 0, y: 60 },
                {
                    opacity: 1, y: 0, duration: 1.1, ease: 'expo.out',
                    scrollTrigger: { trigger: h2Ref.current, start: 'top 85%', toggleActions: 'play none none none' }
                })
            gsap.fromTo(subRef.current, { opacity: 0, y: 30 },
                {
                    opacity: 1, y: 0, duration: 0.9, ease: 'expo.out',
                    scrollTrigger: { trigger: subRef.current, start: 'top 88%', toggleActions: 'play none none none' }
                })
        }, wrapRef)
        return () => ctx.revert()
    }, [])

    return (
        <section ref={wrapRef} id="ecosystem" className="eco-wrap">
            <div className="eco-bg-grid" />
            <div className="eco-inner">
                <div className="eco-header">
                    <div ref={headRef} className="eco-eyebrow" style={{ opacity: 0 }}>
                        <span className="eco-dot" />THE ECOSYSTEM
                    </div>
                    <h2 ref={h2Ref} className="eco-h2" style={{ opacity: 0 }}>
                        Beyond the GCS.<br /><em>We Build the Full Stack.</em>
                    </h2>
                    <p ref={subRef} className="eco-sub" style={{ opacity: 0 }}>
                        DroneCommanderGCS is one piece. Here's the rest of the ecosystem.
                    </p>
                </div>
                <div className="eco-cards" data-count={CARDS.length}>
                    {CARDS.map((card, i) => (
                        <EcoCard key={card.id} data={card} urls={urls} index={i} />
                    ))}
                </div>
            </div>
        </section>
    )
}